// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { useMedplum } from '@medplum/react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';

/**
 * Represents a project membership from Medplum login response
 */
export interface ProjectMembership {
  id: string;
  project: {
    reference: string;
    display?: string;
  };
  profile: {
    reference: string;
    display?: string;
  };
  admin?: boolean;
}

interface MembershipContextValue {
  /** The currently active membership (from current session only) */
  currentMembership: ProjectMembership | undefined;
  /** Set membership after login (session only, not persisted) */
  setCurrentMembership: (membership: ProjectMembership) => void;
  /** Clear membership on logout */
  clearMembership: () => void;
}

const MembershipContext = createContext<MembershipContextValue | undefined>(undefined);

interface MembershipProviderProps {
  children: ReactNode;
}

/**
 * MembershipProvider manages the current project membership for the session.
 *
 * SECURITY: This context does NOT support project switching without re-login.
 * To switch projects, users must:
 * 1. Log out completely
 * 2. Log in again
 * 3. Select the desired project
 *
 * No membership or login data is persisted to localStorage.
 */
export function MembershipProvider({ children }: MembershipProviderProps): JSX.Element {
  const medplum = useMedplum();

  // Session-only state - not persisted
  const [currentMembership, setCurrentMembershipState] = useState<ProjectMembership | undefined>(undefined);

  // Set membership during login (session only)
  const setCurrentMembership = useCallback((membership: ProjectMembership) => {
    setCurrentMembershipState(membership);
    console.log('[MembershipContext] Set current membership:', membership.project.display);
  }, []);

  // Clear membership on logout
  const clearMembership = useCallback(() => {
    setCurrentMembershipState(undefined);
    console.log('[MembershipContext] Cleared membership');
  }, []);

  // Derive current membership from active profile if not explicitly set
  const derivedMembership = useMemo(() => {
    if (currentMembership) return currentMembership;

    // Try to derive from current profile
    const profile = medplum.getProfile();
    if (!profile) return undefined;

    // We don't have full membership info from profile alone
    // This is intentional - membership must be set during login
    return undefined;
  }, [currentMembership, medplum]);

  const value = useMemo(
    () => ({
      currentMembership: derivedMembership,
      setCurrentMembership,
      clearMembership,
    }),
    [derivedMembership, setCurrentMembership, clearMembership]
  );

  return <MembershipContext.Provider value={value}>{children}</MembershipContext.Provider>;
}

export function useMembership(): MembershipContextValue {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership must be used within a MembershipProvider');
  }
  return context;
}
